import React, { useState, useEffect } from 'react';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';
import './IDCards.css';
import IdCardsAdminNav from '../../components/IdCardsAdminNav/IdCardsAdminNav';

const IDCards = () => {
    const { hasRole, hasPermission } = useAuth();
    const { t } = useLanguage();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [members, setMembers] = useState([]);
    const [filters, setFilters] = useState({
        card_status: '',
        payment_status: '',
        search: ''
    });
    const [form, setForm] = useState({
        member_id: '',
        card_type: 'standard',
        payment_amount: 20000,
        remarks: ''
    });

    useEffect(() => {
        loadCards();
        if (hasRole(['admin'])) {
            loadMembers();
        }
    }, [filters]);

    const loadCards = async () => {
        setLoading(true);
        try {
            const res = await api.getIDCards(filters);
            setCards(res.data || []);
        } catch (err) {
            console.error('Failed to load ID cards', err);
            setCards([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMembers = async () => {
        try {
            const res = await api.getMembers({ per_page: 500 });
            setMembers(res.data || []);
        } catch (err) {
            console.error('Failed to load members', err);
        }
    };

    const openForm = () => {
        setForm({
            member_id: '',
            card_type: 'standard',
            payment_amount: 20000,
            remarks: ''
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.createIDCard(form);
            closeForm();
            loadCards();
            alert(t('ID card created successfully'));
        } catch (err) {
            console.error('Create ID card failed', err);
            alert(err.message || t('Failed to create ID card'));
        }
    };

    const handleCardTypeChange = (type) => {
        setForm({
            ...form,
            card_type: type,
            payment_amount: type === 'premium' ? 50000 : 20000
        });
    };

    const openPaymentModal = (card) => {
        setSelectedCard(card);
        setShowPaymentModal(true);
    };

    const handlePaymentConfirm = async (paymentData) => {
        try {
            await api.confirmIDCardPayment(selectedCard.id, paymentData);
            setShowPaymentModal(false);
            setSelectedCard(null);
            loadCards();
            alert(t('Payment confirmed successfully'));
        } catch (err) {
            console.error('Payment confirmation failed', err);
            alert(err.message || t('Failed to confirm payment'));
        }
    };

    const handleStatusUpdate = async (cardId, status) => {
        if (!window.confirm(`${t('Update card status to')} ${status}?`)) return;
        try {
            await api.updateIDCard(cardId, { card_status: status });
            loadCards();
            alert(t('Card status updated'));
        } catch (err) {
            console.error('Status update failed', err);
            alert(err.message || t('Failed to update status'));
        }
    };

    const handlePrint = async (cardId) => {
        try {
            await api.generateIDCardPDF(cardId);
            alert(t('ID Card PDF generated and downloaded successfully'));
        } catch (err) {
            console.error('Print failed', err);
            alert(err.message || t('Failed to generate card'));
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: 'badge-warning',
            active: 'badge-success',
            expired: 'badge-secondary',
            suspended: 'badge-danger',
            lost: 'badge-dark',
            replaced: 'badge-info'
        };
        return statusMap[status] || 'badge-secondary';
    };

    const getPaymentBadge = (status) => {
        const statusMap = {
            pending: 'badge-warning',
            paid: 'badge-success',
            failed: 'badge-danger'
        };
        return statusMap[status] || 'badge-secondary';
    };

    const activeCards = cards.filter(card => card.card_status === 'active').length;
    const pendingCards = cards.filter(card => card.card_status === 'pending').length;
    const paidCards = cards.filter(card => card.payment_status === 'paid').length;

    return (
        <div className="id-cards-page">
            {hasPermission('id_cards.manage') && <IdCardsAdminNav />}
            <div className="page-header id-cards-hero">
                <div className="header-left">
                    <span className="id-kicker">TAREWU • DIGITAL ID REGISTRY</span>
                    <h1 className="page-title">{t('ID Cards Management')}</h1>
                    <p className="page-subtitle">{t('Manage member ID cards and payments')}</p>
                </div>
                {hasRole(['admin']) && (
                    <button className="btn btn-primary" onClick={openForm}>
                        <span>+</span>{t('Issue New Card')}
                    </button>
                )}
            </div>

            <div className="id-summary">
                <div><small>{t('Total Cards')}</small><strong>{cards.length}</strong></div>
                <div><small>{t('Active')}</small><strong>{activeCards}</strong></div>
                <div><small>{t('Pending')}</small><strong>{pendingCards}</strong></div>
                <div><small>{t('Paid')}</small><strong>{paidCards}</strong></div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="filter-group">
                    <label>{t('Card Status:')}</label>
                    <select value={filters.card_status} onChange={(e) => setFilters({...filters, card_status: e.target.value})}>
                        <option value="">{t('All')}</option>
                        <option value="pending">{t('Pending')}</option>
                        <option value="active">{t('Active')}</option>
                        <option value="expired">{t('Expired')}</option>
                        <option value="suspended">{t('Suspended')}</option>
                        <option value="lost">{t('Lost')}</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>{t('Payment Status:')}</label>
                    <select value={filters.payment_status} onChange={(e) => setFilters({...filters, payment_status: e.target.value})}>
                        <option value="">{t('All')}</option>
                        <option value="pending">{t('Pending')}</option>
                        <option value="paid">{t('Paid')}</option>
                        <option value="failed">{t('Failed')}</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>{t('Search:')}</label>
                    <input
                        type="text"
                        placeholder={t('Name or card number...')}
                        value={filters.search}
                        onChange={(e) => setFilters({...filters, search: e.target.value})}
                    />
                </div>
            </div>

            {/* Cards Table */}
            <div className="card id-registry-card">
                {loading ? (
                    <div className="loading-spinner" />
                ) : cards.length === 0 ? (
                    <div className="empty-state">
                        <h3>{t('No ID Cards Found')}</h3>
                        <p>{t('Issue new cards to members')}</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>{t('Card #')}</th>
                                    <th>{t('Member')}</th>
                                    <th>{t('Member #')}</th>
                                    <th>{t('Type')}</th>
                                    <th>{t('Issue Date')}</th>
                                    <th>{t('Expiry')}</th>
                                    <th>{t('Status')}</th>
                                    <th>{t('Payment')}</th>
                                    <th>{t('Amount')}</th>
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cards.map((card) => (
                                    <tr key={card.id}>
                                        <td data-label={t('Card #')} className="card-number-cell">{card.card_number}</td>
                                        <td data-label={t('Member')} className="member-cell"><span className="member-mini-avatar">{`${card.first_name?.[0] || ''}${card.last_name?.[0] || ''}`}</span><b>{card.first_name} {card.last_name}</b></td>
                                        <td data-label={t('Member #')}>{card.member_number}</td>
                                        <td data-label={t('Type')}>
                                            <span className={`badge ${card.card_type === 'premium' ? 'badge-info' : 'badge-secondary'}`}>
                                                {card.card_type}
                                            </span>
                                        </td>
                                        <td data-label={t('Issue Date')}>{new Date(card.issue_date).toLocaleDateString()}</td>
                                        <td data-label={t('Expiry')}>{new Date(card.expiry_date).toLocaleDateString()}</td>
                                        <td data-label={t('Status')}>
                                            <span className={`badge ${getStatusBadge(card.card_status)}`}>
                                                {card.card_status}
                                            </span>
                                        </td>
                                        <td data-label={t('Payment')}>
                                            <span className={`badge ${getPaymentBadge(card.payment_status)}`}>
                                                {card.payment_status}
                                            </span>
                                        </td>
                                        <td data-label={t('Amount')}>TSh {card.payment_amount?.toLocaleString()}</td>
                                        <td data-label={t('Actions')} className="actions-cell">
                                            <div className="action-buttons">
                                                {card.payment_status === 'pending' && hasRole(['admin', 'treasurer']) && (
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => openPaymentModal(card)}
                                                    >
                                                        {t('Confirm Payment')}
                                                    </button>
                                                )}
                                                {card.card_status === 'active' && (
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handlePrint(card.id)}
                                                    >
                                                        {t('Print')}
                                                    </button>
                                                )}
                                                {hasRole(['admin']) && (
                                                    <select
                                                        className="status-select"
                                                        value={card.card_status}
                                                        onChange={(e) => handleStatusUpdate(card.id, e.target.value)}
                                                    >
                                                        <option value="pending">{t('Pending')}</option>
                                                        <option value="active">{t('Active')}</option>
                                                        <option value="suspended">{t('Suspended')}</option>
                                                        <option value="lost">{t('Lost')}</option>
                                                        <option value="replaced">{t('Replaced')}</option>
                                                    </select>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create Card Modal */}
            {showForm && (
                <div className="modal-overlay" onClick={closeForm}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{t('Issue New ID Card')}</h3>
                            <button className="modal-close" onClick={closeForm}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>{t('Select Member *')}</label>
                                    <select
                                        required
                                        value={form.member_id}
                                        onChange={(e) => setForm({...form, member_id: e.target.value})}
                                    >
                                        <option value="">{t('-- Select Member --')}</option>
                                        {members.map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {m.member_number} - {m.first_name} {m.last_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>{t('Card Type *')}</label>
                                    <div className="card-type-selector">
                                        <label className={`card-type-option ${form.card_type === 'standard' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                value="standard"
                                                checked={form.card_type === 'standard'}
                                                onChange={() => handleCardTypeChange('standard')}
                                            />
                                            <div className="card-type-details">
                                                <strong>{t('Standard')}</strong>
                                                <p>{t('Basic ID card - TSh 20,000')}</p>
                                            </div>
                                        </label>
                                        <label className={`card-type-option ${form.card_type === 'premium' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                value="premium"
                                                checked={form.card_type === 'premium'}
                                                onChange={() => handleCardTypeChange('premium')}
                                            />
                                            <div className="card-type-details">
                                                <strong>{t('Premium')}</strong>
                                                <p>{t('Card with Selcom payment integration - TSh 50,000')}</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>{t('Payment Amount')}</label>
                                    <input
                                        type="number"
                                        value={form.payment_amount}
                                        onChange={(e) => setForm({...form, payment_amount: e.target.value})}
                                        readOnly
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('Remarks')}</label>
                                    <textarea
                                        value={form.remarks}
                                        onChange={(e) => setForm({...form, remarks: e.target.value})}
                                        rows="3"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeForm}>
                                    {t('Cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {t('Issue Card')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Payment Confirmation Modal */}
            {showPaymentModal && selectedCard && (
                <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{t('Confirm Payment')}</h3>
                            <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p><strong>{t('Card:')}</strong> {selectedCard.card_number}</p>
                            <p><strong>{t('Member:')}</strong> {selectedCard.first_name} {selectedCard.last_name}</p>
                            <p><strong>{t('Amount:')}</strong> TSh {selectedCard.payment_amount?.toLocaleString()}</p>

                            <div className="form-group">
                                <label>{t('Payment Reference')}</label>
                                <input
                                    type="text"
                                    id="payment-reference"
                                    placeholder={t('Enter payment reference/receipt number')}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowPaymentModal(false)}
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={() => {
                                    const ref = document.getElementById('payment-reference').value;
                                    handlePaymentConfirm({ payment_reference: ref });
                                }}
                            >
                                {t('Confirm Payment')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IDCards;
